/**
 * The share payload for a wildfire, defined once.
 *
 * Two surfaces send a fire somewhere: the map marker's drawer and the report
 * page. They are built from the same `/wildfires` record, so a second inline
 * copy would drift the first time a field moved — the same reason
 * `memberShare.ts` exists.
 *
 * Deliberately excluded: the linked FPA member. A fire may carry `member_id`
 * and `related_member_name` (the backend already withholds both from anyone
 * without `fpa_member_read`), but a share leaves the app entirely. Naming a
 * party in a message that gets forwarded onward is a different act from
 * showing that name to a signed-in staff user, and on an illegal burn it
 * names a suspected offender. Whoever needs that opens the report.
 *
 * Also excluded: internal and external notes, and the burn area — the brief
 * for this payload is where the fire is, when it was, and whether it is out.
 *
 * The one thing the sharer adds by hand is `WildfireResponse` — who is on the
 * way. It is not stored on the fire and is not read back from anywhere: it is
 * a statement the sharer makes at the moment of sending, true only for that
 * message. A toggle left off says nothing at all rather than "not enroute",
 * since the sharer knowing that nobody is coming is a different claim from
 * them not having said.
 */

import { directionsUrl, type SharePayload } from '@/utils/share';
import { wildfireMarkerPosition, type WildfireMapShape } from '@/utils/wildfireMapStyle';

export interface ShareableWildfire extends WildfireMapShape {
  id: number | string;
  fire_reference?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  suburb?: string | null;
  address?: string | null;
  type?: string | null;
}

/**
 * Who the sharer says is on the way. Composed in the share sheet per message;
 * see the note at the top of this file.
 */
export interface WildfireResponse {
  fireDepartmentEnroute?: boolean;
  fpaResourcesEnroute?: boolean;
  /** Names of assisting FPAs / organisations said to be on the way. */
  mutualAidEnroute?: string[];
}

/** Draft / Active / Resolved, matching the badge on both surfaces. */
export function wildfireStateLabel(w: ShareableWildfire): string {
  if (w.review_status === 'draft') return 'Draft';
  return w.is_active ? 'Active' : 'Resolved';
}

const defaultFormat = (value?: string | null): string =>
  value ? new Date(value).toLocaleString() : '';

/**
 * Build the share payload for a wildfire.
 *
 * `formatDateTime` lets a caller reuse its own date formatting so the shared
 * text reads the same as the screen it was shared from.
 */
export function buildWildfireSharePayload(
  wildfire: ShareableWildfire,
  formatDateTime?: (value?: string | null) => string,
  response?: WildfireResponse,
): SharePayload {
  const title = wildfire.fire_reference || `Wildfire #${wildfire.id}`;
  const fmt = formatDateTime ?? defaultFormat;
  // GeoJSON is [lng, lat]; this flips it and falls back to nothing when the
  // fire has no point, in which case the coordinate rows drop out.
  const marker = wildfireMarkerPosition(wildfire);

  return {
    title,
    kind: 'Wildfire',
    headline: title,
    details: [
      // Is it still burning — the first thing anyone reads.
      ['State', wildfireStateLabel(wildfire)],
      ['Type', wildfire.type ? String(wildfire.type).replace(/_/g, ' ') : null],
      // Who is coming, before where it is: a recipient reading only the
      // notification preview learns whether this is a call-out or an FYI.
      ['Fire Department', response?.fireDepartmentEnroute ? 'Enroute' : null],
      ['FPA Resources', response?.fpaResourcesEnroute ? 'Enroute' : null],
      // One row however many were picked: a recipient wants the list, not a
      // line each pushing the coordinates off the notification preview.
      ['Mutual Aid', response?.mutualAidEnroute?.length
        ? `${response.mutualAidEnroute.join(', ')} enroute`
        : null],
      // Then where, coarse to precise, ending in something you can navigate to.
      ['Suburb', wildfire.suburb],
      ['Address', wildfire.address],
      ['Started', wildfire.start_time ? fmt(wildfire.start_time) : null],
      // Absent on an active fire; buildShareText drops empty rows.
      ['Ended', wildfire.end_time ? fmt(wildfire.end_time) : null],
      // Same 5-decimal form as a dropped pin — readable over a radio, and it
      // survives being pasted anywhere a maps link would not.
      ['Coordinates', marker ? `${marker[0].toFixed(5)}, ${marker[1].toFixed(5)}` : null],
      ['Directions', marker ? directionsUrl(marker[0], marker[1]) : null],
    ],
    location: marker ? { lat: marker[0], lng: marker[1], label: title } : null,
  };
}
